// Get Involved Data - How to access services, volunteer, partner, contact
export const GetInvolvedData = {


  volunteer: {
    pageTitle: "Volunteer Opportunities",
    tagline: "Give back, gain skills, strengthen your community",
    whyVolunteer: [
      {
        title: "Make Real Impact",
        description: "Make a direct impact on individuals and families in your community",
        icon: "impact"
      },
      {
        title: "Build Your Career",
        description: "Gain experience in education, advocacy, community work",
        icon: "career"
      },
      {
        title: "Use Your Experience",
        description: "Use your skills (language, professional expertise, lived experience)",
        icon: "skills"
      },
      {
        title: "Community Connection",
        description: "Build connections and be part of a passionate team",
        icon: "connection"
      },
      {
        title: "Fit Around Your Life",
        description: "Flexible opportunities fitting around work, study, caring responsibilities",
        icon: "flexibility"
      },
    ],
    volunteerRoles: [
      {
        role: "ESOL Conversation Partner",
        description:
          "Practice conversational English with ESOL learners in relaxed, friendly setting",
        timeCommitment: "2-4 hours per week, flexible schedule",
        skillsNeeded: "Patience, good communication, cultural sensitivity",
        impact:
          "Help learners build confidence speaking English in real-world contexts",
      },
      {
        role: "Community Language Interpreter",
        description:
          "Support community members in their first language during appointments, assessments, or advocacy",
        timeCommitment: "Ad-hoc, based on availability",
        skillsNeeded:
          "Fluency in English + Arabic/Albanian/Bengali/Urdu/Tigrinya/Farsi",
        impact: "Enable non-English speakers to access critical support",
      },
      {
        role: "Peer Mentor for Job Seekers",
        description:
          "Support individuals through job search, share your employment journey, provide encouragement",
        timeCommitment: "3-5 hours per month",
        skillsNeeded: "Work experience in UK, empathy, motivational skills",
        impact: "Inspire hope and provide practical insider knowledge",
        idealFor: "Those who've navigated UK job market as outsiders",
      },
      {
        role: "Digital Skills Support Volunteer",
        description:
          "Help beginners with computers, tablets, online applications, email",
        timeCommitment: "Flexible - contact us to discuss",
        skillsNeeded: "Confidence with technology, patience teaching basics",
        impact: "Bridge digital divide preventing access to services and jobs",
      },
      {
        role: "Outreach Ambassador",
        description:
          "Connect your faith or cultural community to APT services, build trust, spread awareness",
        timeCommitment: "Flexible - attend community events, share information",
        skillsNeeded: "Connections to specific community, cultural knowledge",
        impact: "Reach isolated individuals who don't know help exists",
        idealFor: "Community leaders, faith group members",
      },
      {
        role: "Event Support Volunteer",
        description:
          "Help with family activities, holiday programs, community events (setup, activities, meals)",
        timeCommitment: "Ad-hoc during school holidays and events",
        skillsNeeded: "Enthusiasm, teamwork, flexibility",
        impact: "Create welcoming community spaces for families",
      },
      {
        role: "Administrative Support",
        description:
          "Help with data entry, filing, reception, phone support",
        timeCommitment: "Half day or full day per week",
        skillsNeeded: "Organizational skills, computer literacy, discretion",
        impact: "Free up staff time to focus on direct support",
      },
      {
        role: "Pro Bono Professional",
        description:
          "Offer skills-based volunteering - legal advice, HR guidance, accounting, marketing",
        timeCommitment: "Flexible, project-based",
        skillsNeeded: "Professional qualification and experience",
        impact: "Provide expertise APT couldn't otherwise afford",
        idealFor: "Lawyers, accountants, HR professionals, marketers",
      },
    ],
    volunteerSupport: {
      description:
        "We value our volunteers and provide comprehensive support:",
      whatWeOffer: [
        "Full induction and ongoing training",
        "Regular supervision and debriefing (especially for advocacy/interpreting roles)",
        "Travel expense reimbursement",
        "References and experience letters for employment/education",
        "Invitation to staff socials and appreciation events",
        "Opportunity to shape programs and give feedback",
      ],
    },
    formerClients: {
      title: "Former Clients: Your Experience Is Valuable",
      description:
        "Many of our best volunteers are people who once received our support. You understand what clients are going through. Your journey inspires hope. Your feedback makes our services better.",
      message:
        "If APT helped you and you want to give back - even just a few hours a month - we'd love to hear from you.",
    },
    howToVolunteer: {
      process: [
        "Contact us expressing interest (email, phone, or in person)",
        "Informal chat about your skills, interests, and availability",
        "DBS check if working with vulnerable people (we cover cost)",
        "Induction and role-specific training",
        "Start volunteering with ongoing support",
      ],
      contact: "Email: info@alisarprospects.training or call our main number",
    },
  },

  partner: {
    pageTitle: "Partner With Us",
    tagline: "Collaborate to amplify community impact",
    forEmployers: {
      title: "Employers: Hire Diverse Talent",
      description:
        "Our learners bring resilience, diverse perspectives, and strong motivation. Many have significant skills from international careers, caring, or community leadership - overlooked by traditional recruitment.",
      opportunities: [
        {
          type: "Hire Our Graduates",
          description:
            "We can introduce you to qualified, work-ready candidates who've completed vocational training and employment preparation",
          benefits: [
            "Access to motivated, pre-screened candidates",
            "Candidates with industry qualifications (Customer Service, Health & Social Care, Business Admin)",
            "We provide ongoing support to new hires (reducing turnover)",
          ],
        },
        {
          type: "Offer Work Placements",
          description:
            "Provide work experience to learners building towards employment",
          benefits: [
            "Trial potential employees before commitment",
            "Fulfill corporate social responsibility goals",
            "Bring fresh energy and diverse perspectives to your team",
          ],
        },
        {
          type: "Sponsor Apprenticeships",
          description:
            "We can support your apprenticeship recruitment and development",
          benefits: [
            "Access apprenticeship funding",
            "We handle educational components and support",
            "Develop talent pipeline aligned with your business needs",
          ],
        },
        {
          type: "Flexible Employment Practices",
          description:
            "Many of our clients need flexible hours (single parents, carers) - if you can offer this, we'll connect great candidates",
          benefits: [
            "Tap into underutilized talent pool",
            "High loyalty from employees whose needs are respected",
            "Diverse workforce strengthens business resilience",
          ],
        },
      ],
      contactInfo:
        "Interested in hiring or providing work placements? Contact our Employment Team: [email]",
    },
    forOrganizations: {
      title: "Community Organizations: Collaborate for Impact",
      description:
        "We partner with organizations sharing our commitment to community empowerment. Let's coordinate support, avoid duplication, and meet holistic needs.",
      partnershipModels: [
        {
          model: "Co-Location & Service Integration",
          description:
            "We can deliver services at your site (community center, faith space, family hub)",
          examples: [
            "ESOL classes at your community center",
            "Job search drop-in at your hub",
            "Advocacy surgeries at your location",
          ],
        },
        {
          model: "Cross-Referral Network",
          description:
            "We refer clients to your services (mental health, legal, debt advice) and you refer to ours (education, employment, advocacy)",
          examples: [
            "We refer to your mental health support; you refer to our ESOL",
            "We refer to your debt advice; you refer to our employment support",
          ],
        },
        {
          model: "Joint Outreach & Events",
          description:
            "Co-deliver community events, outreach to underserved populations, pooled resources",
          examples: [
            "Joint outreach to temporary accommodation",
            "Co-hosted community information events",
            "Shared translation and interpretation resources",
          ],
        },
        {
          model: "Shared Training & Resources",
          description:
            "Collaborate on staff training, share best practices, develop joint programs",
          examples: [
            "Trauma-informed practice training",
            "Cultural competency workshops",
            "Co-designed programs for specific communities",
          ],
        },
      ],
      currentPartners:
        "We currently partner with Integration Hubs, Newham Law Centre, Citizens Advice, local faith organizations, and more. Join this network!",
      contactInfo:
        "Interested in partnership? Contact our Director: [email]",
    },
    forFunders: {
      title: "Funders: Invest in Community-Led Change",
      description:
        "We're a small, agile CIC delivering outsized impact with minimal overheads. Every pound goes directly to community benefit.",
      fundingNeeds: [
        {
          priority: "Unrestricted Core Funding",
          description:
            "Sustain essential staff, cover rent and operations, respond flexibly to emerging needs",
          impact:
            "Enables us to say 'yes' to community members without restrictive eligibility criteria",
        },
        {
          priority: "ESOL & Adult Education Expansion",
          description:
            "Demand for ESOL far exceeds capacity - we have waiting lists",
          impact:
            "Every £10k funds 20+ asylum seekers/refugees through 12-week ESOL course",
        },
        {
          priority: "Advocacy & Rights Support",
          description:
            "Complex cases (housing, benefits, immigration) require intensive staff time",
          impact:
            "£20k funds advocacy coordinator for 100+ cases annually, securing £100k+ in benefits for clients",
        },
        {
          priority: "Employment Support Expansion",
          description:
            "Growing demand for job search and career planning support",
          impact:
            "£15k funds employment adviser supporting 50+ job seekers, with 45%+ employment outcome rate",
        },
      ],
      whyFundAPT: [
        "Community-led with lived experience on board and staff",
        "Proven outcomes: 45.7% employment rate, 78-85% course completion, £45k+ benefits secured",
        "Small enough to be agile, established enough to be sustainable (11+ years)",
        "Holistic approach addressing root causes, not just symptoms",
        "Multiplier effect: former clients become volunteers and community leaders",
      ],
      contactInfo:
        "Interested in funding partnership? Contact our Director: [email]",
    },
  },

  contact: {
    pageTitle: "Contact Us",
    tagline: "Reach out - we're here to help",
    mainContact: {
      address: "[Insert full address]",
      phone: "[Insert phone number]",
      email: "[Insert general email]",
      whatsapp: "[Insert WhatsApp number]",
    },
    dropInHours: {
      days: "Monday to Friday",
      hours: "10:00 AM - 4:00 PM",
      note: "No appointment needed - just come in and we'll see you",
    },
    specificInquiries: [
      {
        topic: "Course Enrollments & Education",
        email: "[education@apt.org]",
        phone: "[education team phone]",
      },
      {
        topic: "Employment Support & Job Search",
        email: "[employment@apt.org]",
        phone: "[employment team phone]",
      },
      {
        topic: "Advocacy & Rights Support (Urgent Cases)",
        email: "[advocacy@apt.org]",
        phone: "[advocacy team phone]",
        note: "For emergency eviction/sanctions, call and say it's urgent",
      },
      {
        topic: "Volunteer Opportunities",
        email: "[volunteer@apt.org]",
        phone: "[volunteer coordinator phone]",
      },
      {
        topic: "Partnership & Collaboration",
        email: "[director@apt.org]",
        attention: "Emi Gjoni, Director",
      },
      {
        topic: "Media & Press Inquiries",
        email: "[media@apt.org]",
      },
    ],
    languages: {
      description: "We offer support in multiple languages:",
      available: [
        "English",
        "Arabic",
        "Albanian",
        "Bengali",
        "Urdu",
        "Tigrinya",
        "Farsi (interpreter can be arranged)",
      ],
      note: "Let us know your language preference when you contact us",
    },
    socialMedia: {
      facebook: "[Facebook page]",
      instagram: "[Instagram handle]",
      twitter: "[Twitter handle]",
      linkedin: "[LinkedIn page]",
      whatsappCommunity: "[WhatsApp community link for updates]",
    },
    mailingList: {
      description:
        "Join our mailing list for updates on new programs, events, and opportunities",
      signupLink: "[Insert signup link or form]",
    },
    feedbackComplaints: {
      title: "Feedback & Complaints",
      description:
        "We want to hear from you - what we're doing well and where we can improve.",
      process: [
        "Informal feedback: Tell any staff member anytime",
        "Formal feedback: Email [feedback@apt.org] or fill out feedback form at reception",
        "Complaints: We take concerns seriously. Email [complaints@apt.org] or request complaints procedure at reception",
      ],
      commitment:
        "We'll acknowledge complaints within 3 working days and aim to resolve within 2 weeks",
    },
  },
};

// Quick reference for navigation
export const GetInvolvedCategories = [
  {
    id: "volunteer",
    title: "Volunteer Opportunities",
    url: "/get-involved/volunteer",
  },
  {
    id: "partner",
    title: "Partner With Us",
    url: "/get-involved/partner",
  },
  {
    id: "contact",
    title: "Contact",
    url: "/contact",
  },
];
